import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    TemplateRef,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import {
    takeUntilDestroyed,
    toObservable,
    toSignal,
} from '@angular/core/rxjs-interop';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import { leaveWith, LiveStatusComponent } from '@interacta-shared/ui';
import { deltaToPlainText } from '@interacta-shared/util-common';
import { Store } from '@ngrx/store';
import { filter, map, pairwise } from 'rxjs';
import {
    closeToastNotificationPanel,
    expandToastNotificationPanel,
} from '../../store/toast-notification.actions';
import {
    selectToastNotificationExpanded,
    selectToastNotificationGroups,
    selectToastNotificationVisible,
} from '../../store/toast-notification.selectors';
import { ToastNotificationPanelComponent } from '../toast-notification-panel/toast-notification-panel.component';

@Component({
    imports: [LiveStatusComponent, ToastNotificationPanelComponent],
    selector: 'interacta-toast-notification-overlay',
    templateUrl: './toast-notification-overlay.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastNotificationOverlayComponent implements OnDestroy {
    private readonly store = inject(Store);
    private readonly overlay = inject(Overlay);
    private readonly vcr = inject(ViewContainerRef);
    private readonly gaService = inject(GoogleAnalyticsService);

    private overlayRef: OverlayRef | null = null;

    readonly groups = this.store.selectSignal(selectToastNotificationGroups);
    readonly expanded = this.store.selectSignal(
        selectToastNotificationExpanded,
    );
    readonly isVisible = this.store.selectSignal(
        selectToastNotificationVisible,
    );

    readonly announcement = toSignal(
        this.store.select(selectToastNotificationGroups).pipe(
            pairwise(),
            map(([previous, current]) => {
                const text = current
                    .filter(({ id }) => !previous.some((p) => p.id === id))
                    .flatMap(({ messages }) => messages)
                    .map((message) => deltaToPlainText(message).trim())
                    .filter((message) => message.length > 0)
                    .join('. ');
                return text.length > 0 ? text : null;
            }),
        ),
        { initialValue: null },
    );

    private readonly panelContent =
        viewChild.required<TemplateRef<unknown>>('panelContent');

    constructor() {
        toObservable(this.isVisible)
            .pipe(takeUntilDestroyed(), filter(Boolean))
            .subscribe(() => this.attachPanel());
    }

    readonly leave = leaveWith('animate-slide-out', () => {
        this.overlayRef?.dispose();
        this.overlayRef = null;
    });

    close(): void {
        this.gaService.sendCustomEvent('isp_trigger_toast_dismissed');
        this.store.dispatch(closeToastNotificationPanel());
    }

    expand(): void {
        this.gaService.sendCustomEvent('isp_trigger_toast_opened');
        this.store.dispatch(expandToastNotificationPanel());
    }

    ngOnDestroy(): void {
        this.overlayRef?.dispose();
    }

    private attachPanel(): void {
        if (this.overlayRef?.hasAttached()) {
            return;
        }

        this.overlayRef = this.overlay.create({
            positionStrategy: this.overlay
                .position()
                .global()
                .top('4.5rem')
                .right('1rem'),
            panelClass: ['w-352', 'xl:w-440'],
        });
        this.overlayRef.attach(
            new TemplatePortal(this.panelContent(), this.vcr),
        );
    }
}
